<template>
  <v-data-table
    :headers="_headers"
    :items="transmittals"
    :search="search"
    :loading="loading"
    group-by="programCode"
    sort-by="uploadedAt"
    sort-desc
    item-key="transmittalId"
    v-model="selected"
    show-select
  >
    <template v-if="isProcessed" v-slot:[`top`]>
      <v-toolbar flat>
        <v-tooltip
          v-for="action in actions"
          :key="action.name"
          top
          :disabled="!action.tooltip"
        >
          <template v-slot:activator="{ on }">
            <!-- wrapper so the tooltip still shows while the button is disabled -->
            <span v-on="on">
              <v-btn
                small
                :disabled="!actionEnabled(action)"
                color="primary"
                class="mx-1"
                @click="performAction(action)"
                >{{ action.name }}</v-btn
              >
            </span>
          </template>
          <span>{{ action.tooltip }}</span>
        </v-tooltip>
        <confirm-modal ref="confirm"></confirm-modal>
      </v-toolbar>
    </template>
    <template v-slot:[`header.data-table-select`]>
      <v-simple-checkbox
        :value="allSelectableSelected"
        :indeterminate="someSelectableSelected"
        :disabled="selectableTransmittals.length === 0"
        @input="toggleSelectAll"
      ></v-simple-checkbox>
    </template>
    <template
      v-slot:[`group.header`]="{ group, headers, toggle, isOpen, items }"
      ><td :colspan="headers.length">
        <v-btn @click="toggle" x-small icon :ref="group">
          <v-icon v-if="isOpen">mdi-minus</v-icon>
          <v-icon v-else>mdi-plus</v-icon>
        </v-btn>
        <span class="mx-5 font-weight-bold text-subtitle-2">
          {{ items[0].programCode }}
        </span>
      </td>
    </template>
    <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
      <v-simple-checkbox
        :value="isSelected"
        @input="select($event)"
        :disabled="!Boolean(item.processedAt)"
      ></v-simple-checkbox>
    </template>
    <template v-slot:[`item.status`]="{ item }">
      <v-chip :color="item.statusColor" label x-small text-color="white">{{
        item.status
      }}</v-chip>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-btn
        v-if="item.isError"
        text
        color="primary"
        x-small
        @click="reprocess(item)"
        >Reprocess</v-btn
      >
      <resolve-transmittal-dialog
        v-if="item.isError"
        :transmittalId="item.transmittalId"
      ></resolve-transmittal-dialog>
    </template>
  </v-data-table>
</template>
<script>
import ConfirmModal from "../Shared/confirm-modal";
import ResolveTransmittalDialog from "./resolve-transmittal-dialog.vue";
export default {
  props: ["loading", "transmittals", "search", "isProcessed"],
  components: { ConfirmModal, ResolveTransmittalDialog },
  data() {
    return {
      headers: [
        { text: "Id", value: "transmittalId" },
        { text: "Program", value: "programCode" },
        { text: "Semester", value: "semesterCode" },
        { text: "Client", value: "clientName" },
        { text: "OSIS", value: "clientOsis" },
        { text: "District", value: "districtId" },
        { text: "Transmitted School", value: "schoolName" },
        { text: "Registered School", value: "registeredSchoolName" },
        { text: "Service", value: "serviceCode" },
        { text: "Mandate", value: "mandate" },
        { text: "Service Start Date", value: "formattedServiceStartDate" },
        { text: "Status", value: "status" },
        { text: "Message", value: "message" },
        { text: "Actions", value: "actions", hide: this.isProcessed },
      ],
      selected: [],
      actions: [
        {
          name: "Accept",
          path: "acceptTransmittals",
          subtitle:
            "This will create service plan(s) if no matching one(s) exist",
          presentParticiple: "Accepting",
        },
        {
          // only offered once a normal Accept has been blocked by the overlap rule
          name: "Accept as Duplicate",
          path: "acceptTransmittals",
          asDuplicate: true,
          requiresOverlapBlocked: true,
          tooltip:
            "Enabled only when every selected transmittal was already blocked by an overlapping service plan on a previous Accept",
          subtitle:
            'This will create a new, overlapping service plan flagged "Duplicate as per district instructions".',
          presentParticiple: "Accepting",
        },
        {
          name: "Reject",
          path: "rejectTransmittals",
          subtitle:
            "Any associated service plans will be unlinked, but not deleted.",
          presentParticiple: "Rejecting",
        },
        {
          name: "Revoke",
          path: "revokeTransmittals",
          subtitle:
            "Any associated service plans will be unlinked, but not deleted.",
          presentParticiple: "Revoking",
        },
        {
          name: "Refuse",
          path: "refuseTransmittals",
          subtitle: "",
          presentParticiple: "Refusing",
        },
      ],
    };
  },
  computed: {
    _headers() {
      return this.headers.filter((x) => !x.hide);
    },
    selectableTransmittals() {
      return this.transmittals.filter((t) => Boolean(t.processedAt));
    },
    allSelectableSelected() {
      return (
        this.selectableTransmittals.length > 0 &&
        this.selected.length === this.selectableTransmittals.length
      );
    },
    someSelectableSelected() {
      return this.selected.length > 0 && !this.allSelectableSelected;
    },
  },
  watch: {
    // selections survive a change to the filters that produced them, so drop
    // the ones that are no longer in the list - actions must never run on
    // transmittals the user can't see
    transmittals(list) {
      if (this.selected.length === 0) return;
      const ids = new Set(list.map((t) => t.transmittalId));
      this.selected = this.selected.filter((t) => ids.has(t.transmittalId));
    },
  },
  methods: {
    toggleSelectAll() {
      this.selected = this.allSelectableSelected
        ? []
        : [...this.selectableTransmittals];
    },
    // "Accept as Duplicate" is only usable once every selected transmittal has been blocked by the
    // overlap rule on a prior Accept - i.e. the user has already seen that error message
    actionEnabled(action) {
      if (this.selected.length === 0) return false;
      if (action.requiresOverlapBlocked) {
        return this.selected.every((t) => t.isOverlapBlocked);
      }
      return true;
    },
    async performAction(action) {
      if (
        await this.$refs.confirm.open(
          `${action.name} ${this.selected.length} transmittal(s)?`,
          action.subtitle,
          action.name
        )
      ) {
        this.$store.commit("uxModule/setShowLoader", true);
        const transmittalIds = this.selected.map((t) => t.transmittalId);
        // accept takes a richer payload (with the duplicate flag); the other actions take ids
        const payload =
          action.path === "acceptTransmittals"
            ? { transmittalIds, asDuplicate: Boolean(action.asDuplicate) }
            : transmittalIds;
        var { success, message } = await this.$store.dispatch(
          "transmittalsModule/" + action.path,
          payload
        );
        this.$store.commit("uxModule/setShowLoader", false);

        if (success) {
          this.selected = [];
        }
        if (!success || message) {
          this.$store.commit(
            "uxModule/setSnackbarMsg",
            success
              ? message
              : `Error ${action.presentParticiple} Transmittal(s): ${message}`
          );
          this.$store.commit("uxModule/setShowSnackbar", true);
        }
      }
    },
    async reprocess(transmittal) {
      this.$store.commit("uxModule/setShowLoader", true);
      var message = await this.$store.dispatch(
        "transmittalsModule/reprocessTransmittal",
        { transmittalId: transmittal.transmittalId }
      );
      this.$store.commit("uxModule/setShowLoader", false);

      if (message) {
        this.$store.commit(
          "uxModule/setSnackbarMsg",
          `Error Reprocessing Transmittal: ${message}`
        );
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
    },
  },
};
</script>
